/**
 * Paywall Screen (hard block)
 *
 * Shown in place of the app when the signed-in user has no active or trialing
 * subscription. Presents the three paid tiers, lets them subscribe via Stripe
 * Checkout, redeem a school code, or sign out.
 *
 * The tier cards are rendered from /billing/plans — the same config the backend
 * enforces — so the limits shown here can never drift from the limits applied.
 */

import { useState } from 'react';
import { toast } from 'sonner';
import { Check, LogOut, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useStartCheckout, usePlans } from '@/hooks/queries';
import { getErrorMessage } from '@/integrations/api/client';
import {
  formatPrice,
  monthlyEquivalent,
  type PlanDefinition,
  type PlanInterval,
  type PlanTier,
  type SubscriptionStatus,
} from '@/services/billing';

const F = "'DM Sans', sans-serif";

const INK = '#2A2926';
const MUTED = '#9B978E';
const BODY = '#6B675E';
const RULE = '#E8E6E1';
const TEAL = '#3ECDC6';

/** The tier we point most families at. */
const RECOMMENDED: PlanTier = 'family';

/** A short, status-aware line so a lapsed user understands why they're here. */
function headlineFor(status: SubscriptionStatus | undefined): { title: string; sub: string } {
  switch (status) {
    case 'past_due':
    case 'unpaid':
      return {
        title: 'Your payment needs attention',
        sub: 'We couldn’t process your last payment. Update your billing to restore access.',
      };
    case 'canceled':
      return {
        title: 'Ready to continue the journey?',
        sub: "Pick up right where you left off — all your family's stories and memories are waiting.",
      };
    default:
      return {
        title: 'Choose your Yoluno',
        sub: 'Every plan starts with a 14-day free trial. Cancel anytime — no questions asked.',
      };
  }
}

/** The four lines that actually differ between tiers. */
function planPoints(plan: PlanDefinition): string[] {
  const { limits } = plan;
  const stories =
    limits.storiesPerMonth === null
      ? 'Unlimited stories, fair use'
      : `${limits.storiesPerMonth} stories a month, pooled across the family`;
  const voices =
    limits.lovedOneVoices === 1
      ? '1 loved-one voice'
      : `${limits.lovedOneVoices} loved-one voices`;
  const points = [
    `${limits.childProfiles} child ${limits.childProfiles === 1 ? 'profile' : 'profiles'}`,
    stories,
    voices,
  ];
  if (limits.priorityStories) points.push('Priority story generation');
  if (limits.earlyAccess) points.push('Early access to new features');
  return points;
}

function TierCard({
  plan,
  interval,
  featured,
  busy,
  onChoose,
}: {
  plan: PlanDefinition;
  interval: PlanInterval;
  featured: boolean;
  busy: boolean;
  onChoose: () => void;
}) {
  const price = plan.price!;
  const amount = interval === 'year' ? price.year : price.month;

  return (
    <div
      className="flex flex-col rounded-2xl bg-white p-6 transition"
      style={{
        border: featured ? `2px solid ${TEAL}` : `1px solid ${RULE}`,
        boxShadow: featured ? '0 12px 32px -18px rgba(42,41,38,0.35)' : 'none',
      }}
    >
      {/* Always-present badge row: keeps all three headings, prices and buttons on
          one baseline rather than letting the featured card push itself down. */}
      <div className="mb-3 flex items-start" style={{ height: 26 }}>
        {featured && (
          <span
            className="rounded-full px-3 py-1 text-xs font-semibold"
            style={{ background: 'rgba(62,205,198,0.16)', color: '#2FA9A3', fontFamily: F }}
          >
            Best for most families
          </span>
        )}
      </div>

      <h3 style={{ fontFamily: F, fontSize: 18, fontWeight: 700, color: INK }}>{plan.name}</h3>
      {/* Tall enough for the longest tagline (3 lines) so prices stay aligned. */}
      <p style={{ fontFamily: F, fontSize: 13.5, color: MUTED, marginTop: 4, lineHeight: 1.45, minHeight: 58 }}>
        {plan.tagline}
      </p>

      <div className="mt-4">
        <span style={{ fontFamily: F, fontSize: 34, fontWeight: 600, color: INK }}>
          {formatPrice(amount)}
        </span>
        <span style={{ fontFamily: F, fontSize: 14, color: MUTED, marginLeft: 5 }}>
          {interval === 'year' ? '/year' : '/month'}
        </span>
        {/* Reserve the line in both states so the three cards keep a shared baseline. */}
        <p style={{ fontFamily: F, fontSize: 12.5, color: MUTED, marginTop: 4, minHeight: 18 }}>
          {interval === 'year' ? `${monthlyEquivalent(price.year)}/mo billed yearly` : ''}
        </p>
      </div>

      <button
        onClick={onChoose}
        disabled={busy}
        className="mt-5 w-full rounded-full py-3 text-sm font-semibold transition hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
        style={{
          fontFamily: F,
          background: featured ? TEAL : 'transparent',
          color: featured ? '#FFFFFF' : INK,
          border: featured ? 'none' : `1px solid ${INK}`,
        }}
      >
        {busy ? 'Redirecting…' : `Start free trial`}
      </button>

      <ul className="mt-5 flex flex-col gap-2.5 pt-5" style={{ borderTop: `1px solid ${RULE}` }}>
        {planPoints(plan).map((point) => (
          <li
            key={point}
            className="flex items-start gap-2.5"
            style={{ fontFamily: F, fontSize: 13.5, color: INK, lineHeight: 1.45 }}
          >
            <Check size={15} style={{ color: TEAL, marginTop: 2, flexShrink: 0 }} />
            {point}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Paywall({ status }: { status?: SubscriptionStatus }) {
  const { user, signOut } = useAuth();
  const [interval, setInterval] = useState<PlanInterval>('year');
  const [showCode, setShowCode] = useState(false);
  const [code, setCode] = useState('');
  const [pendingTier, setPendingTier] = useState<PlanTier | null>(null);

  const checkout = useStartCheckout();
  const { data: catalogue, isLoading, isError } = usePlans();

  const { title, sub } = headlineFor(status);

  const start = (tier: PlanTier, promoCode?: string) => {
    setPendingTier(tier);
    checkout.mutate(
      { tier, interval, ...(promoCode ? { promoCode } : {}) },
      {
        onError: (err) => {
          setPendingTier(null);
          toast.error(getErrorMessage(err));
        },
      }
    );
  };

  // A school code is redeemed against the recommended tier — a classroom needs
  // the full allowance, and the code path grants a 3-month no-card trial on it.
  const handleRedeem = () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    start(RECOMMENDED, trimmed);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 py-12" style={{ background: '#FAF9F7' }}>
      <div className="w-full max-w-5xl">
        <div className="text-center mb-8">
          <h1 style={{ fontFamily: F, fontWeight: 600, fontSize: 28, color: INK }}>{title}</h1>
          <p style={{ fontFamily: F, fontSize: 15, color: BODY, marginTop: 8, lineHeight: 1.5 }}>{sub}</p>
        </div>

        {/* Interval toggle */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <button
            onClick={() => setInterval('month')}
            className="px-4 py-2 rounded-full text-sm font-medium transition"
            style={{
              fontFamily: F,
              background: interval === 'month' ? INK : 'transparent',
              color: interval === 'month' ? '#FFFFFF' : MUTED,
            }}
          >
            Monthly
          </button>
          <button
            onClick={() => setInterval('year')}
            className="px-4 py-2 rounded-full text-sm font-medium transition flex items-center gap-2"
            style={{
              fontFamily: F,
              background: interval === 'year' ? INK : 'transparent',
              color: interval === 'year' ? '#FFFFFF' : MUTED,
            }}
          >
            Annual
            <span
              className="text-xs px-2 py-0.5 rounded-full"
              style={{ background: 'rgba(62,205,198,0.2)', color: '#2FA9A3' }}
            >
              2 months free
            </span>
          </button>
        </div>

        {isLoading && (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin" size={26} style={{ color: TEAL }} />
          </div>
        )}

        {isError && (
          <p className="text-center" style={{ fontFamily: F, fontSize: 14, color: BODY }}>
            We couldn’t load the plans just now. Refresh the page to try again.
          </p>
        )}

        {catalogue && (
          <div className="grid gap-5 md:grid-cols-3">
            {catalogue.plans.map((plan) => (
              <TierCard
                key={plan.tier}
                plan={plan}
                interval={interval}
                featured={plan.tier === RECOMMENDED}
                busy={checkout.isPending && pendingTier === plan.tier}
                onChoose={() => start(plan.tier)}
              />
            ))}
          </div>
        )}

        {/* Shared reassurance + school code */}
        <div className="mt-8 text-center">
          <p style={{ fontFamily: F, fontSize: 13, color: MUTED }}>
            Safety, characters and parent controls are identical on every plan. They’re the product,
            not an upsell.
          </p>

          <div className="mt-5">
            {!showCode ? (
              <button
                onClick={() => setShowCode(true)}
                className="text-sm font-medium transition hover:opacity-70"
                style={{ fontFamily: F, color: BODY }}
              >
                Have a school code?
              </button>
            ) : (
              <div className="flex items-center gap-2 max-w-sm mx-auto">
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleRedeem();
                  }}
                  placeholder="Enter school code"
                  autoFocus
                  aria-label="School code"
                  className="flex-1 px-4 py-2.5 rounded-full border text-sm outline-none focus:ring-2"
                  style={{ fontFamily: F, borderColor: RULE, color: INK }}
                />
                <button
                  onClick={handleRedeem}
                  disabled={checkout.isPending || !code.trim()}
                  className="px-5 py-2.5 rounded-full text-sm font-semibold text-white transition disabled:opacity-50"
                  style={{ background: INK, fontFamily: F }}
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer: account + sign out */}
        <div className="flex items-center justify-center gap-2 mt-10">
          <span style={{ fontFamily: F, fontSize: 13, color: MUTED }}>{user?.email}</span>
          <span style={{ color: RULE }}>·</span>
          <button
            onClick={signOut}
            className="flex items-center gap-1.5 text-sm font-medium"
            style={{ color: BODY, fontFamily: F }}
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

export default Paywall;
